import { useEffect, useMemo, useState } from 'react';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Funnel, Info } from 'shared/icons';
import { useTokens } from '../../pages/operacoes/components/tokens';
import api from '../../services/api';
import { CountLabel, FormatDecimal1, FormatInt, FormatMoney } from '../../utils/format';
import { Pill } from '../campaigns/campaignCardShared';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import {
    DASH,
    DetailStat,
    ExpandCaret,
    FormatWeekLabels,
    GroupByToggle,
    IntelLoading,
    IntelStatCard,
    MlbLink,
    RowThumb,
    SkuChip,
} from './intelligenceShared';

/**
 * DEMANDA (Inteligência de Mercado) — previsão de demanda e curva de
 * crescimento, da LOJA e por anúncio/SKU.
 *
 * Duas leituras na mesma tela: em cima a curva da loja (semanas reais, reta
 * de tendência e semanas projetadas com faixa de 80%, em unidades ou
 * receita, mais a tabela mensal); embaixo a lista por item com tendência,
 * giro recente, projeção do horizonte escolhido e acurácia do backtest. A
 * lista é ordenada/filtrada/paginada no SERVIDOR (a projeção só existe
 * depois do motor), então as colunas são sortable:false — mesmo padrão da
 * Elasticidade. O detalhe inline mostra o gráfico do item.
 */

const HORIZON_OPTIONS = [
    { value: 4, label: 'Próximas 4 semanas (~30 dias)' },
    { value: 8, label: 'Próximas 8 semanas (~60 dias)' },
    { value: 13, label: 'Próximas 13 semanas (~90 dias)' },
];

const MONTHS_OPTIONS = [
    { value: 6, label: 'Últimos 6 meses' },
    { value: 12, label: 'Últimos 12 meses' },
    { value: 24, label: 'Últimos 24 meses' },
];

/** Tendência do wire → rótulo + tom. danger = queda (é o que pede ação). */
const TREND_META = {
    crescendo: { label: 'Crescendo', tone: 'success' },
    estavel: { label: 'Estável', tone: 'neutral' },
    caindo: { label: 'Caindo', tone: 'danger' },
    intermitente: { label: 'Intermitente', tone: 'warning' },
    parado: { label: 'Parado', tone: 'mono' },
    historico_curto: { label: 'Histórico curto', tone: 'mono' },
};

const TREND_OPTIONS = [
    { value: 'all', label: 'Todas as tendências' },
    ...Object.entries(TREND_META).map(([value, meta]) => ({ value, label: meta.label })),
];

const SORT_OPTIONS = [
    { value: 'forecast', label: 'Maior previsão' },
    { value: 'growth', label: 'Maior crescimento' },
    { value: 'decline', label: 'Maior queda' },
    { value: 'recent', label: 'Mais vendidos (4 sem.)' },
];

const CONFIDENCE_PILLS = {
    alta: { label: 'Alta', tone: 'success' },
    media: { label: 'Média', tone: 'warning' },
    baixa: { label: 'Baixa', tone: 'mono' },
};

/** Método do motor → como explicar de onde saiu o número. */
const METHOD_LABELS = {
    holt_damped: 'tendência amortecida',
    croston: 'demanda intermitente',
    parado: 'sem venda há 8+ semanas',
};

const FORECAST_REASONS = {
    insufficient_history: 'Menos de 4 semanas completas de venda — sem base para projetar.',
};

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];

/**
 * Linha secundária da tendência. O "% ao mês" só aparece quando a reta do
 * último trimestre é significativa (crescendo/caindo) — em item "estável" a
 * inclinação existe numericamente mas é ruído, e "+54% ao mês" ao lado de
 * "Estável" leria como contradição. Nos demais casos, o dado que explica a
 * classe: semanas paradas, semanas de histórico.
 */
function describeTrend(row) {
    switch (row.trend) {
        case 'crescendo':
        case 'caindo':
            return `${formatSignedPct(row.growth_pct_per_month)} ao mês (últimas 12 sem.)`;
        case 'estavel':
            return 'sem direção clara no trimestre';
        case 'parado':
            return `${CountLabel(row.zero_sales_streak_weeks, 'semana', 'semanas')} sem venda`;
        case 'intermitente':
            return 'venda esparsa (Croston)';
        default:
            return `${CountLabel(row.weeks_available, 'semana', 'semanas')} de histórico`;
    }
}

/** "+12,3%" / "-7,0%" / '—' (sinal explícito: a direção É a informação). */
function formatSignedPct(value) {
    if (value === null || value === undefined) {
        return DASH;
    }
    const v = Number(value);
    if (!Number.isFinite(v)) {
        return DASH;
    }
    return `${v > 0 ? '+' : ''}${FormatDecimal1(v)}%`;
}

/** Faixa "a – b" com o formatador da métrica; '—' quando não há faixa. */
function formatRange(low, high, format) {
    if (low === null || low === undefined || high === null || high === undefined) {
        return DASH;
    }
    return `${format(low)} – ${format(high)}`;
}

/** Unidades projetadas: inteiro quando ≥ 10, 1 casa abaixo (fração de unidade é média, não venda). */
function formatUnits(value) {
    if (value === null || value === undefined) {
        return DASH;
    }
    const v = Number(value);
    if (!Number.isFinite(v)) {
        return DASH;
    }
    return Math.abs(v) >= 10 ? FormatInt(Math.round(v)) : FormatDecimal1(v);
}

const TrendPill = ({ trend }) => {
    const meta = TREND_META[trend];
    return <Pill tone={meta?.tone || 'mono'}>{meta?.label || trend || DASH}</Pill>;
};

const ConfidencePill = ({ confidence }) => {
    if (!confidence) {
        return <span className="text-mono-400">{DASH}</span>;
    }
    const meta = CONFIDENCE_PILLS[confidence];
    return <Pill tone={meta?.tone || 'mono'}>{meta?.label || confidence}</Pill>;
};

/* ── Gráfico semanal: real + tendência + projeção com faixa ──────────────── */

/**
 * `weeks`: [{ week_start, value, trend }] (semanas completas, reais);
 * `forecastWeeks`: [{ week_start, value, low, high }] (projeção, começa na
 * semana corrente). A última semana real repete o valor na série de
 * projeção para a linha tracejada NASCER da barra, não flutuar solta.
 */
const ForecastChart = ({ weeks, forecastWeeks, formatValue, unitLabel, T }) => {
    const series = useMemo(() => {
        const out = [];
        const actual = Array.isArray(weeks) ? weeks : [];
        const projected = Array.isArray(forecastWeeks) ? forecastWeeks : [];
        actual.forEach((week, index) => {
            const labels = FormatWeekLabels(week.week_start);
            const value = Number(week.value || 0);
            const isLast = index === actual.length - 1 && projected.length > 0;
            out.push({
                weekStart: week.week_start,
                label: labels.short,
                fullLabel: labels.full,
                actual: value,
                trend: week.trend === null || week.trend === undefined ? null : Number(week.trend),
                forecast: isLast ? value : null,
                band: isLast ? [value, value] : null,
                kind: 'actual',
            });
        });
        for (const week of projected) {
            const labels = FormatWeekLabels(week.week_start);
            out.push({
                weekStart: week.week_start,
                label: labels.short,
                fullLabel: labels.full,
                actual: null,
                trend: null,
                forecast: Number(week.value || 0),
                band: [Number(week.low || 0), Number(week.high || 0)],
                kind: 'forecast',
            });
        }
        return out;
    }, [weeks, forecastWeeks]);

    if (series.length === 0) {
        return <p className="py-6 text-sm text-mono-500">Sem série semanal no período.</p>;
    }

    const firstForecast = series.find((point) => point.kind === 'forecast');

    const ChartTooltip = ({ active, payload }) => {
        if (!active || !payload || payload.length === 0) {
            return null;
        }
        const p = payload[0].payload;
        return (
            <div
                style={{
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    padding: '6px 10px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: T.mono,
                    color: T.ink,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    pointerEvents: 'none',
                }}
            >
                <div style={{ color: T.muted, marginBottom: 2 }}>semana de {p.fullLabel}</div>
                {p.kind === 'actual' ? (
                    <>
                        <div style={{ fontWeight: 600 }}>
                            {formatValue(p.actual)} {unitLabel}
                        </div>
                        {p.trend !== null ? (
                            <div style={{ color: T.muted }}>tendência {formatValue(p.trend)}</div>
                        ) : null}
                    </>
                ) : (
                    <>
                        <div style={{ fontWeight: 600 }}>
                            projeção {formatValue(p.forecast)} {unitLabel}
                        </div>
                        <div style={{ color: T.muted }}>
                            faixa {formatValue(p.band[0])} – {formatValue(p.band[1])}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div>
            <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                    <XAxis
                        dataKey="label"
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        interval={Math.max(0, Math.floor(series.length / 10) - 1)}
                    />
                    <YAxis
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        width={48}
                        allowDecimals={false}
                        tickFormatter={(value) => FormatInt(value)}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: T.lineMid, strokeDasharray: '2 2' }} />
                    {firstForecast ? (
                        <ReferenceLine x={firstForecast.label} stroke={T.lineMid} strokeDasharray="4 2" />
                    ) : null}
                    {/* Faixa de 80% da projeção: Area de intervalo (dataKey em par [low, high]). */}
                    <Area
                        dataKey="band"
                        stroke="none"
                        fill={T.ok}
                        fillOpacity={0.15}
                        connectNulls={false}
                        isAnimationActive={false}
                        activeDot={false}
                    />
                    <Bar
                        dataKey="actual"
                        fill={T.accent}
                        fillOpacity={0.75}
                        maxBarSize={18}
                        radius={[2, 2, 0, 0]}
                        isAnimationActive={false}
                    />
                    <Line
                        type="linear"
                        dataKey="trend"
                        stroke={T.muted}
                        strokeWidth={1.5}
                        strokeDasharray="6 3"
                        dot={false}
                        activeDot={false}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="forecast"
                        stroke={T.ok}
                        strokeWidth={2}
                        strokeDasharray="5 3"
                        dot={{ r: 2.5, fill: T.ok, stroke: 'none' }}
                        activeDot={{ r: 4, fill: T.ok, stroke: T.bg, strokeWidth: 2 }}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
            {/* Legenda manual: a nativa não explica faixa nem linha tracejada. */}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-mono-500">
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ background: T.accent, opacity: 0.75 }}
                    />
                    {unitLabel}/semana (real)
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-0 w-4 border-t-2 border-dashed" style={{ borderColor: T.muted }} />
                    reta de tendência
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-0 w-4 border-t-2 border-dashed" style={{ borderColor: T.ok }} />
                    projeção
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: T.ok, opacity: 0.25 }} />
                    faixa provável (80%)
                </span>
            </div>
        </div>
    );
};

/* ── Curva de crescimento da loja ────────────────────────────────────────── */

const METRIC_OPTIONS = [
    { value: 'units', label: 'Unidades' },
    { value: 'revenue', label: 'Receita' },
];

const MONTH_NAMES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "2026-08" → "ago/2026" (string slicing puro, sem fuso). */
function formatMonthLabel(month) {
    const [year, monthIndex] = String(month ?? '').split('-');
    const name = MONTH_NAMES[Number(monthIndex) - 1];
    return name ? `${name}/${year}` : String(month ?? DASH);
}

const GrowthCurvePanel = ({ months, horizon }) => {
    const T = useTokens();
    const [metric, setMetric] = useState('units');
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [retryTick, setRetryTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true, error: null }));
        api.getMarketIntelligenceGrowthCurve({ months, horizon_weeks: horizon })
            .then((payload) => {
                if (!cancelled) {
                    setState({ loading: false, error: null, data: payload || null });
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setState({
                        loading: false,
                        error: err?.message || 'Não foi possível carregar a curva de crescimento.',
                        data: null,
                    });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [months, horizon, retryTick]);

    const data = state.data;
    const isRevenue = metric === 'revenue';
    const block = data?.[metric] || null;
    const formatValue = isRevenue ? (value) => FormatMoney(value) : formatUnits;
    const unitLabel = isRevenue ? 'R$' : 'un';

    const chartWeeks = useMemo(
        () =>
            (Array.isArray(data?.weeks) ? data.weeks : []).map((week) => ({
                week_start: week.week_start,
                value: isRevenue ? week.revenue : week.units,
                trend: isRevenue ? week.revenue_trend_line : week.units_trend_line,
            })),
        [data, isRevenue],
    );
    const chartForecast = useMemo(
        () =>
            (Array.isArray(data?.forecast_weeks) ? data.forecast_weeks : []).map((week) => ({
                week_start: week.week_start,
                value: isRevenue ? week.revenue : week.units,
                low: isRevenue ? week.revenue_low : week.units_low,
                high: isRevenue ? week.revenue_high : week.units_high,
            })),
        [data, isRevenue],
    );

    const monthRows = Array.isArray(data?.months) ? data.months : [];

    return (
        <div className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-sm font-semibold text-black dark:text-white">Curva de crescimento da loja</p>
                    <p className="mt-0.5 text-xs text-mono-500">
                        Todas as vendas do período (inclusive de anúncios hoje pausados), semana a semana.
                        {data?.current_week ? (
                            <>
                                {' '}
                                Semana corrente ({FormatInt(data.current_week.days_elapsed)}{' '}
                                {data.current_week.days_elapsed === 1 ? 'dia' : 'dias'}):{' '}
                                {isRevenue
                                    ? FormatMoney(data.current_week.revenue)
                                    : `${FormatInt(data.current_week.units)} un`}
                                .
                            </>
                        ) : null}
                    </p>
                </div>
                <div className="inline-flex shrink-0 rounded-lg border border-mono-300 p-0.5 dark:border-mono-700">
                    {METRIC_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => setMetric(option.value)}
                            aria-pressed={metric === option.value}
                            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                                metric === option.value
                                    ? 'bg-black font-medium text-white dark:bg-white dark:text-black'
                                    : 'text-mono-600 hover:bg-mono-100 dark:text-mono-400 dark:hover:bg-mono-900'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {state.error ? (
                <div className="mt-4">
                    <ErrorCard
                        title="Erro ao carregar a curva"
                        message={state.error}
                        action="Tentar novamente"
                        actionFn={() => setRetryTick((tick) => tick + 1)}
                    />
                </div>
            ) : null}
            {state.loading && !data ? <IntelLoading text="Carregando curva de crescimento…" /> : null}

            {data && !state.error ? (
                <>
                    {state.loading ? <IntelLoading text="Recalculando…" /> : null}
                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <IntelStatCard
                            label="Tendência"
                            value={<TrendPill trend={block?.trend} />}
                            sub={block ? describeTrend({ ...block, weeks_available: data.weeks_available }) : null}
                        />
                        <IntelStatCard
                            label="Últimas 4 semanas"
                            value={formatValue(block?.last_4w)}
                            sub={
                                block?.recent_vs_previous_pct !== null && block?.recent_vs_previous_pct !== undefined
                                    ? `${formatSignedPct(block.recent_vs_previous_pct)} vs. 4 anteriores (${formatValue(block?.prev_4w)})`
                                    : 'Sem 4 semanas anteriores para comparar'
                            }
                        />
                        <IntelStatCard
                            label={`Projeção ${HORIZON_OPTIONS.find((option) => option.value === horizon)?.label.replace('Próximas ', '') || ''}`}
                            value={block?.forecast ? formatValue(block.forecast.total) : DASH}
                            tone={block?.forecast ? 'info' : 'mono'}
                            sub={
                                block?.forecast
                                    ? `faixa ${formatRange(block.forecast.low, block.forecast.high, formatValue)} · ${METHOD_LABELS[block.forecast.method] || block.forecast.method}`
                                    : FORECAST_REASONS[block?.forecast_reason] || 'Sem projeção'
                            }
                        />
                        <IntelStatCard
                            label="Acurácia (backtest)"
                            value={<ConfidencePill confidence={block?.accuracy?.confidence} />}
                            sub={
                                block?.accuracy
                                    ? `erro ${block.accuracy.wape_pct === null ? DASH : `${FormatDecimal1(block.accuracy.wape_pct)}%`} nas últimas ${CountLabel(block.accuracy.holdout_weeks, 'semana', 'semanas')} escondidas · viés ${formatSignedPct(block.accuracy.bias_pct)}`
                                    : 'Histórico curto para testar'
                            }
                        />
                    </div>

                    <div className="mt-4">
                        <ForecastChart
                            weeks={chartWeeks}
                            forecastWeeks={chartForecast}
                            formatValue={formatValue}
                            unitLabel={unitLabel}
                            T={T}
                        />
                    </div>

                    <div className="mt-4 border-t border-mono-200 pt-3 dark:border-mono-800">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-mono-400">Mês a mês</p>
                        <div className="mt-2">
                            <StandardTable
                                variant="minimal"
                                rows={[...monthRows].reverse()}
                                rowKey={(row) => row.month}
                                emptyMessage="Sem meses inteiros na janela."
                                columns={MONTH_COLUMNS}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

// Tabela mensal ordena no CLIENTE (é pequena): getValue numérico para o sort
// não virar lexicográfico; FormatMoney só no render.
const MONTH_COLUMNS = [
    {
        key: 'month',
        label: 'Mês',
        getValue: (row) => row.month,
        render: (row) => (
            <div>
                <span className="text-sm font-medium text-black dark:text-white">{formatMonthLabel(row.month)}</span>
                {row.partial ? (
                    <p className="mt-0.5 text-[11px] text-mono-500">
                        parcial · {FormatInt(row.days_elapsed)}/{FormatInt(row.days_in_month)} dias
                    </p>
                ) : null}
            </div>
        ),
    },
    {
        key: 'units',
        label: 'Unidades',
        align: 'right',
        getValue: (row) => Number(row.units || 0),
        render: (row) => (
            <div>
                <span className="tabular-nums">{FormatInt(row.units)}</span>
                {row.partial && row.projected_units !== null ? (
                    <p className="mt-0.5 text-[11px] tabular-nums text-mono-500">
                        ritmo → {FormatInt(row.projected_units)}
                    </p>
                ) : null}
            </div>
        ),
    },
    {
        key: 'revenue',
        label: 'Receita',
        align: 'right',
        getValue: (row) => Number(row.revenue || 0),
        render: (row) => (
            <div>
                <span className="font-semibold tabular-nums">{FormatMoney(row.revenue)}</span>
                {row.partial && row.projected_revenue !== null ? (
                    <p className="mt-0.5 text-[11px] tabular-nums text-mono-500">
                        ritmo → {FormatMoney(row.projected_revenue)}
                    </p>
                ) : null}
            </div>
        ),
    },
    {
        key: 'units_mom_pct',
        label: 'Var. unidades',
        align: 'right',
        getValue: (row) => (row.units_mom_pct === null ? Number.NEGATIVE_INFINITY : Number(row.units_mom_pct)),
        render: (row) => <SignedPctCell value={row.units_mom_pct} />,
    },
    {
        key: 'revenue_mom_pct',
        label: 'Var. receita',
        align: 'right',
        getValue: (row) => (row.revenue_mom_pct === null ? Number.NEGATIVE_INFINITY : Number(row.revenue_mom_pct)),
        render: (row) => <SignedPctCell value={row.revenue_mom_pct} />,
    },
];

/** Variação com cor pela direção (verde sobe, vermelho cai, neutro ~0). */
const SignedPctCell = ({ value }) => {
    if (value === null || value === undefined) {
        return <span className="text-mono-400">{DASH}</span>;
    }
    const v = Number(value);
    const color =
        v > 1
            ? 'text-success-600 dark:text-success-400'
            : v < -1
              ? 'text-danger-600 dark:text-danger-400'
              : 'text-mono-500';
    return <span className={`tabular-nums text-sm ${color}`}>{formatSignedPct(v)}</span>;
};

/* ── Detalhe inline do item ──────────────────────────────────────────────── */

const DemandDetail = ({ row, months, horizon, groupBy }) => {
    const T = useTokens();
    const [detail, setDetail] = useState({ loading: true, error: null, data: null });
    const [retryTick, setRetryTick] = useState(0);

    const groupKey = row?.group_key;
    useEffect(() => {
        if (!groupKey) {
            return undefined;
        }
        let cancelled = false;
        setDetail({ loading: true, error: null, data: null });
        api.getMarketIntelligenceDemandForecastItem(groupKey, { months, horizon_weeks: horizon, group_by: groupBy })
            .then((payload) => {
                if (!cancelled) {
                    setDetail({ loading: false, error: null, data: payload || null });
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setDetail({
                        loading: false,
                        error: err?.message || 'Não foi possível carregar a série semanal.',
                        data: null,
                    });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [groupKey, months, horizon, groupBy, retryTick]);

    if (!row) {
        return null;
    }
    const data = detail.data;
    const forecast = row.forecast;
    const chartWeeks = (Array.isArray(data?.weeks) ? data.weeks : []).map((week) => ({
        week_start: week.week_start,
        value: week.units,
        trend: week.trend_line,
    }));
    const chartForecast = (Array.isArray(data?.forecast_weeks) ? data.forecast_weeks : []).map((week) => ({
        week_start: week.week_start,
        value: week.units,
        low: week.low,
        high: week.high,
    }));
    const listings = Array.isArray(data?.listings) ? data.listings : [];

    return (
        <div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-mono-200 pb-3 dark:border-mono-800">
                <DetailStat label="Tendência" value={<TrendPill trend={row.trend} />} sub={describeTrend(row)} />
                <DetailStat
                    label={`Projeção ${CountLabel(horizon, 'semana', 'semanas')}`}
                    value={forecast ? `${formatUnits(forecast.units)} un` : DASH}
                    sub={
                        forecast
                            ? `faixa ${formatRange(forecast.units_low, forecast.units_high, formatUnits)} · ${METHOD_LABELS[forecast.method] || forecast.method}`
                            : FORECAST_REASONS[row.forecast_reason] || null
                    }
                />
                <DetailStat
                    label="Por semana / por dia"
                    value={
                        forecast
                            ? `${FormatDecimal1(forecast.weekly_avg)} / ${FormatDecimal1(forecast.daily_avg)}`
                            : DASH
                    }
                />
                <DetailStat
                    label="Últimas 4 semanas"
                    value={`${FormatInt(row.units_last_4w)} un`}
                    sub={
                        row.units_prev_4w !== null && row.units_prev_4w !== undefined
                            ? `${formatSignedPct(row.recent_vs_previous_pct)} vs. ${FormatInt(row.units_prev_4w)} nas 4 anteriores`
                            : null
                    }
                />
                <DetailStat label="Média 12 sem." value={`${FormatDecimal1(row.units_weekly_avg_12w)}/sem`} />
                <DetailStat
                    label="Semana corrente"
                    value={`${FormatInt(row.current_week?.units)} un`}
                    sub={`${CountLabel(row.current_week?.days_elapsed || 0, 'dia', 'dias')} decorridos`}
                />
                <DetailStat
                    label="Acurácia"
                    value={<ConfidencePill confidence={row.accuracy?.confidence} />}
                    sub={
                        row.accuracy
                            ? `erro ${row.accuracy.wape_pct === null ? DASH : `${FormatDecimal1(row.accuracy.wape_pct)}%`} · viés ${formatSignedPct(row.accuracy.bias_pct)} (${CountLabel(row.accuracy.holdout_weeks, 'semana', 'semanas')})`
                            : 'histórico curto para testar'
                    }
                />
            </div>

            <div className="mt-4">
                {detail.loading ? <IntelLoading text="Carregando série semanal…" /> : null}
                {detail.error ? (
                    <div className="flex flex-wrap items-center gap-3 py-4">
                        <p className="text-sm text-danger-600 dark:text-danger-400">{detail.error}</p>
                        <button
                            type="button"
                            onClick={() => setRetryTick((tick) => tick + 1)}
                            className="rounded-lg border border-mono-300 px-3 py-1.5 text-sm text-mono-700 transition-colors hover:bg-mono-100 dark:border-mono-700 dark:text-mono-300 dark:hover:bg-mono-900"
                        >
                            Tentar novamente
                        </button>
                    </div>
                ) : null}
                {!detail.loading && !detail.error && data ? (
                    <>
                        <ForecastChart
                            weeks={chartWeeks}
                            forecastWeeks={chartForecast}
                            formatValue={formatUnits}
                            unitLabel="un"
                            T={T}
                        />
                        {chartForecast.length > 0 ? (
                            <div className="mt-4 border-t border-mono-200 pt-3 dark:border-mono-800">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-mono-400">
                                    Semanas projetadas
                                </p>
                                <ul className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3 lg:grid-cols-4">
                                    {chartForecast.map((week) => (
                                        <li key={week.week_start} className="flex items-baseline justify-between gap-2">
                                            <span className="text-mono-500">
                                                {FormatWeekLabels(week.week_start).short}
                                            </span>
                                            <span className="tabular-nums text-black dark:text-white">
                                                <span className="font-semibold">{formatUnits(week.value)}</span>
                                                <span className="ml-1 text-xs text-mono-500">
                                                    ({formatUnits(week.low)}–{formatUnits(week.high)})
                                                </span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                        {groupBy === 'sku' ? (
                            <div className="mt-4 border-t border-mono-200 pt-3 dark:border-mono-800">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-mono-400">
                                    Anúncios deste SKU
                                </p>
                                {listings.length === 0 ? (
                                    <p className="mt-2 text-sm text-mono-500">Nenhum anúncio detalhado no período.</p>
                                ) : (
                                    <ul className="mt-2 space-y-1.5">
                                        {listings.map((listing) => (
                                            <li
                                                key={listing.item_id}
                                                className="flex flex-wrap items-center gap-2 text-sm"
                                            >
                                                <MlbLink itemId={listing.item_id} />
                                                <span
                                                    className="min-w-0 flex-1 truncate text-mono-700 dark:text-mono-300"
                                                    title={listing.title || ''}
                                                >
                                                    {listing.title || DASH}
                                                </span>
                                                <span className="shrink-0 tabular-nums text-mono-500">
                                                    {FormatInt(listing.units)} un
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );
};

/* ── Colunas da lista ────────────────────────────────────────────────────── */

// sortable:false em todas: a ordem é do servidor (param `sort`), e um
// cabeçalho clicável que não reordena seria UX enganosa (mesmo padrão da
// Elasticidade). getValue alimenta só o export CSV.
function buildMetricColumns(horizon) {
    return [
        {
            key: 'trend',
            label: 'Tendência',
            sortable: false,
            getValue: (row) => TREND_META[row.trend]?.label || row.trend || '',
            render: (row) => (
                <div className="min-w-0">
                    <TrendPill trend={row.trend} />
                    <p className="mt-1 text-xs tabular-nums text-mono-500">{describeTrend(row)}</p>
                </div>
            ),
        },
        {
            key: 'units_last_4w',
            label: 'Últimas 4 sem.',
            align: 'right',
            sortable: false,
            searchable: false,
            getValue: (row) => Number(row.units_last_4w ?? 0),
            render: (row) => (
                <div>
                    <span className="tabular-nums">{FormatInt(row.units_last_4w)} un</span>
                    <p className="mt-0.5 text-xs tabular-nums text-mono-500">
                        {row.recent_vs_previous_pct === null || row.recent_vs_previous_pct === undefined
                            ? `média ${FormatDecimal1(row.units_weekly_avg_12w)}/sem`
                            : `${formatSignedPct(row.recent_vs_previous_pct)} vs. anteriores`}
                    </p>
                </div>
            ),
        },
        {
            key: 'forecast_units',
            label: `Previsão ${CountLabel(horizon, 'sem.', 'sem.')}`,
            align: 'right',
            sortable: false,
            searchable: false,
            getValue: (row) => (row.forecast ? Number(row.forecast.units) : ''),
            render: (row) =>
                row.forecast ? (
                    <div>
                        <span className="font-semibold tabular-nums text-black dark:text-white">
                            {formatUnits(row.forecast.units)} un
                        </span>
                        <p className="mt-0.5 text-xs tabular-nums text-mono-500">
                            {formatRange(row.forecast.units_low, row.forecast.units_high, formatUnits)}
                        </p>
                    </div>
                ) : (
                    <span className="text-mono-400" title={FORECAST_REASONS[row.forecast_reason] || ''}>
                        {DASH}
                    </span>
                ),
        },
        {
            key: 'daily_avg',
            label: 'Por dia',
            align: 'right',
            sortable: false,
            searchable: false,
            getValue: (row) => (row.forecast ? Number(row.forecast.daily_avg) : ''),
            render: (row) =>
                row.forecast ? (
                    <div>
                        <span className="tabular-nums">{FormatDecimal1(row.forecast.daily_avg)}</span>
                        <p className="mt-0.5 text-xs tabular-nums text-mono-500">
                            {FormatDecimal1(row.forecast.weekly_avg)}/sem
                        </p>
                    </div>
                ) : (
                    <span className="text-mono-400">{DASH}</span>
                ),
        },
        {
            key: 'accuracy',
            label: 'Acurácia',
            align: 'center',
            sortable: false,
            getValue: (row) =>
                row.accuracy?.wape_pct === null || row.accuracy?.wape_pct === undefined
                    ? ''
                    : Number(row.accuracy.wape_pct),
            render: (row) => (
                <div className="flex flex-col items-center gap-1">
                    <ConfidencePill confidence={row.accuracy?.confidence} />
                    {row.accuracy && row.accuracy.wape_pct !== null ? (
                        <span className="text-[11px] tabular-nums text-mono-500">
                            erro {FormatDecimal1(row.accuracy.wape_pct)}%
                        </span>
                    ) : null}
                </div>
            ),
        },
    ];
}

function buildColumns(groupBy, expandedKey, horizon) {
    const identityColumn =
        groupBy === 'sku'
            ? {
                  key: 'item',
                  label: 'SKU',
                  sortable: false,
                  getValue: (row) => `${row.sku || ''} ${row.title || ''}`.trim(),
                  render: (row) => (
                      <div className="flex min-w-0 items-center gap-3">
                          <ExpandCaret open={expandedKey === row.group_key} />
                          <RowThumb src={row.thumbnail} />
                          <div className="min-w-0">
                              <p className="truncate font-mono text-sm font-semibold text-black dark:text-white">
                                  {row.sku || DASH}
                              </p>
                              <p className="mt-0.5 truncate text-xs text-mono-500" title={row.title || ''}>
                                  {CountLabel(row.listings_count, 'anúncio', 'anúncios')}
                                  {row.title ? ` · ${row.title}` : ''}
                              </p>
                          </div>
                      </div>
                  ),
              }
            : {
                  key: 'item',
                  label: 'Produto',
                  sortable: false,
                  getValue: (row) => `${row.title || ''} ${row.sku || ''}`.trim(),
                  render: (row) => (
                      <div className="flex min-w-0 items-center gap-3">
                          <ExpandCaret open={expandedKey === row.group_key} />
                          <RowThumb src={row.thumbnail} />
                          <div className="min-w-0">
                              <p
                                  className="truncate text-sm font-medium text-black dark:text-white"
                                  title={row.title || ''}
                              >
                                  {row.title || DASH}
                              </p>
                              <p className="mt-0.5 flex items-center gap-2">
                                  <MlbLink itemId={row.item_id} />
                                  {row.sku ? <SkuChip sku={row.sku} /> : null}
                              </p>
                          </div>
                      </div>
                  ),
              };
    return [identityColumn, ...buildMetricColumns(horizon)];
}

/* ── Painel ──────────────────────────────────────────────────────────────── */

const DemandForecast = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState(null);
    const [skipped, setSkipped] = useState(null);
    const [months, setMonths] = useState(12);
    const [horizon, setHorizon] = useState(4);
    const [trend, setTrend] = useState('all');
    const [sort, setSort] = useState('forecast');
    const [groupBy, setGroupBy] = useState('item');
    // Busca aplica no blur/Enter (padrão da Elasticidade): um request por
    // tecla recalcularia o catálogo inteiro a cada dígito.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [expandedKey, setExpandedKey] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
                const payload = await api.getMarketIntelligenceDemandForecast({
                    months,
                    horizon_weeks: horizon,
                    trend,
                    sort,
                    group_by: groupBy,
                    search,
                    limit: pageSize,
                    offset,
                });
                if (!cancelled) {
                    const list = Array.isArray(payload?.items) ? payload.items : [];
                    setRows(list);
                    setTotal(Number(payload?.total ?? list.length));
                    setSummary(payload?.summary ?? null);
                    setSkipped(payload?.skipped ?? null);
                    setExpandedKey((prev) => (prev && list.some((row) => row.group_key === prev) ? prev : null));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar a previsão de demanda.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setHasLoadedOnce(true);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [months, horizon, trend, sort, groupBy, search, page, pageSize, refreshTick]);

    const changeFilter = (setter) => (value) => {
        setter(value);
        setPage(1);
    };

    const changeGroupBy = (value) => {
        if (value === groupBy) {
            return;
        }
        setGroupBy(value);
        setExpandedKey(null);
        setPage(1);
    };

    const applySearch = () => {
        const term = searchInput.trim();
        setSearchInput(term);
        if (term !== search) {
            setSearch(term);
            setPage(1);
        }
    };

    const isSkuMode = groupBy === 'sku';
    const columns = useMemo(() => buildColumns(groupBy, expandedKey, horizon), [groupBy, expandedKey, horizon]);

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={6} rows={8} />
    ) : error ? (
        <ErrorCard
            title="Erro ao carregar a previsão"
            message={error}
            action="Tentar novamente"
            actionFn={() => setRefreshTick((tick) => tick + 1)}
        />
    ) : (
        <>
            {loading ? <IntelLoading text="Recalculando projeções…" /> : null}
            <StandardTable
                rows={rows}
                columns={columns}
                rowKey={(row) => row.group_key}
                onRowClick={(row) => setExpandedKey((prev) => (prev === row.group_key ? null : row.group_key))}
                rowClassName={(row) => (expandedKey === row.group_key ? 'bg-mono-50 dark:bg-mono-900/60' : '')}
                renderExpandedRow={(row) =>
                    expandedKey === row.group_key ? (
                        <DemandDetail row={row} months={months} horizon={horizon} groupBy={groupBy} />
                    ) : null
                }
                countLabel={isSkuMode ? 'SKUs analisados' : 'itens analisados'}
                emptyMessage={
                    search
                        ? `Nenhum item encontrado para “${search}” — confira o termo ou limpe a busca.`
                        : 'Nenhum item no recorte — afrouxe o filtro de tendência ou aumente a janela.'
                }
                exportFileName={isSkuMode ? 'previsao-demanda-sku' : 'previsao-demanda'}
                searchEnabled={false}
                sortMode="server"
                paginationMode="server"
                totalItems={total}
                currentPage={page}
                onPageChange={setPage}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
                defaultPageSize={DEFAULT_PAGE_SIZE}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
        </>
    );

    return (
        <div className="space-y-4">
            {/* O que a projeção é e NÃO é: sem isto, "previsão 120 un" vira
                pedido de compra. É o histórico próprio extrapolado — não vê
                campanha futura, ruptura de estoque nem sazonalidade além da
                janela. */}
            <div className="flex items-start gap-2 rounded-xl border border-info-200 bg-info-50 p-4 dark:border-info-800 dark:bg-info-900/20">
                <Info size={20} className="mt-0.5 shrink-0 text-info-600 dark:text-info-400" />
                <div className="text-sm text-info-800 dark:text-info-200">
                    <p>
                        Projeta as <strong>unidades das próximas semanas</strong> a partir do seu próprio histórico
                        semanal (tendência amortecida; Croston para itens de venda esparsa) e mede a{' '}
                        <strong>curva de crescimento</strong> de cada item e da loja (reta sobre as últimas 12 semanas
                        completas; a série de cada item começa no primeiro giro regular). A faixa é o cenário provável
                        (80%), e a acurácia vem de um backtest: o modelo esconde as últimas semanas e tenta acertá-las.
                        Leia como triagem de reposição: a projeção <strong>não enxerga</strong> campanha futura, ruptura
                        de estoque nem sazonalidade fora da janela — uma semana em promoção entra como demanda normal.
                    </p>
                    {summary ? (
                        <p className="mt-2 text-xs text-info-700 dark:text-info-300">
                            Cobertura: {FormatInt(summary.analyzed)} {isSkuMode ? 'SKUs' : 'itens'} analisados ·{' '}
                            {FormatInt(summary.crescendo)} crescendo · {FormatInt(summary.estavel)} estáveis ·{' '}
                            {FormatInt(summary.caindo)} caindo · {FormatInt(summary.intermitente)} intermitentes ·{' '}
                            {FormatInt(summary.parado)} parados · {FormatInt(summary.historico_curto)} com histórico
                            curto
                            {skipped?.insufficient_history
                                ? ` (${FormatInt(skipped.insufficient_history)} sem projeção)`
                                : ''}
                            {isSkuMode && skipped?.no_sku
                                ? ` · ${FormatInt(skipped.no_sku)} anúncios sem SKU ficaram de fora`
                                : ''}
                            . Projeção somada: {formatUnits(summary.forecast_units_total)} un em{' '}
                            {CountLabel(horizon, 'semana', 'semanas')} (últimas 4:{' '}
                            {FormatInt(summary.units_last_4w_total)} un).
                        </p>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="w-64">
                    <Select
                        label="Horizonte"
                        value={horizon}
                        onChange={(event) => changeFilter(setHorizon)(Number(event.target.value))}
                    >
                        {HORIZON_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-48">
                    <Select
                        label="Histórico"
                        value={months}
                        onChange={(event) => changeFilter(setMonths)(Number(event.target.value))}
                    >
                        {MONTHS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
            </div>

            <GrowthCurvePanel months={months} horizon={horizon} />

            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] max-w-md flex-1">
                    <Input
                        label="Buscar"
                        type="search"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        onBlur={applySearch}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                applySearch();
                            }
                        }}
                        placeholder="Buscar MLB, SKU ou título..."
                    />
                </div>
                <div className="w-52">
                    <Select
                        label="Tendência"
                        value={trend}
                        onChange={(event) => changeFilter(setTrend)(event.target.value)}
                    >
                        {TREND_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-52">
                    <Select
                        label="Ordenar por"
                        value={sort}
                        onChange={(event) => changeFilter(setSort)(event.target.value)}
                    >
                        {SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <GroupByToggle value={groupBy} onChange={changeGroupBy} />
                {trend !== 'all' || search ? (
                    <button
                        type="button"
                        onClick={() => {
                            setTrend('all');
                            setSearchInput('');
                            setSearch('');
                            setPage(1);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-mono-300 px-3 py-2 text-sm text-mono-700 transition-colors hover:bg-mono-100 dark:border-mono-700 dark:text-mono-300 dark:hover:bg-mono-900"
                    >
                        <Funnel size={14} />
                        Limpar filtros
                    </button>
                ) : null}
            </div>

            {listContent}
        </div>
    );
};

export default DemandForecast;
